import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Backdrop from "@mui/material/Backdrop";
import { IoChevronBackCircleSharp } from "react-icons/io5";
import { IoMdAdd } from "react-icons/io";
import { FaRegThumbsUp, FaThumbsUp } from "react-icons/fa";
import Header from "./Header";
import AlertMessage from "../../common/components/AlertMessage";
import * as APIService from "../../common/api/index.js";
import URL from "../../common/api/ConstantURL";
import { getSessionContext } from "../../common/utils/sessionContext";
import { formatNoteDateTime, isNoteAcknowledged, useNotesTruncation } from "../../common/utils/noteUtils";
import * as Sentry from "@sentry/react";
import "../../styles/Notes.css";

const Notes = () => {
  const navigate = useNavigate();
  const { residentName, residentId, caregiverId, communityId } = getSessionContext();
  const [notes, setNotes] = useState([]);
  const [myNotes, setMyNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alertStatus, setAlertStatus] = useState("");
  const [expandedNotes, setExpandedNotes] = useState({});
  const [activeNotesTab, setActiveNotesTab] = useState("all");
  const [truncatedNotes, setTruncatedNotes] = useState({});
  const messageNodesRef = useRef({});

  const openAddNote = () => navigate("/add-notes");

  const loadSharedNotes = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        caregiverId: caregiverId || "",
        clientId: residentId || "",
        communityId: communityId || "",
      }).toString();
      const responseData = await APIService.getData(`${URL.getNotesAddedByOtherCaregiversForClient}?${queryParams}`);
      const sharedSuccess = responseData?.status === "success" || responseData?.code === 200;
      if (!sharedSuccess) {
        setAlertStatus({
          message: responseData?.message || "Failed to fetch shared notes",
          status: "400",
          origin: "center",
        });
        return;
      }
      const sharedNotes = Array.isArray(responseData?.data) ? responseData.data : [];
      setNotes(sharedNotes);
    } catch (error) {
      Sentry.captureException(error);
      setAlertStatus({
        message: "Error while fetching shared notes",
        status: "400",
        origin: "center",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadMyNotes = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        caregiverId: caregiverId || "",
        clientId: residentId || "",
        communityId: communityId || "",
      }).toString();
      const responseData = await APIService.getData(`${URL.getNotesAddedByCaregiver}?${queryParams}`);
      if (responseData?.status != "success") {
        setAlertStatus({
          message: responseData?.message || "Failed to fetch my notes",
          status: "400",
          origin: "center",
        });
        return;
      }
      const ownNotes = Array.isArray(responseData?.data) ? responseData.data : [];
      setMyNotes(ownNotes);
    } catch (error) {
      Sentry.captureException(error);
      setAlertStatus({
        message: "Error while fetching my notes",
        status: "400",
        origin: "center",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tabKey) => {
    setActiveNotesTab(tabKey);
    setAlertStatus("");
    if (tabKey === "my") {
      loadMyNotes();
      return;
    }
    loadSharedNotes();
  };

  useEffect(() => {
    if (!residentId) {
      setAlertStatus({
        message: "Resident is not selected.",
        status: "400",
        origin: "center",
      });
      return;
    }

    loadSharedNotes();
  }, [residentId, caregiverId, communityId]);

  const toggleExpanded = (noteId) => {
    setExpandedNotes((prev) => ({
      ...prev,
      [noteId]: !prev[noteId],
    }));
  };

  const visibleNotes = activeNotesTab === "my" ? myNotes : notes;

  const setMessageNode = (noteId) => (node) => {
    if (node) {
      messageNodesRef.current[noteId] = node;
    } else {
      delete messageNodesRef.current[noteId];
    }
  };

  useNotesTruncation({
    notes: visibleNotes,
    expandedNotes,
    messageNodesRef,
    setTruncatedNotes,
    getNoteId: (note) => note.noteId,
  });

  const toggleLikeNote = (noteId) => {
    const targetNote = notes.find((note) => String(note.noteId) === String(noteId));
    if (!targetNote) return;

    if (isNoteAcknowledged(targetNote)) {
      return;
    }

    const acknowledgeNote = async () => {
      setLoading(true);
      try {
        const query = new URLSearchParams({
          noteId: targetNote.noteId || targetNote.id,
          caregiverId: caregiverId || "",
        }).toString();
        const responseData = await APIService.putData(`${URL.acknowledgeNote}?${query}`);
        const acknowledgeSuccess = responseData?.status === "success" || responseData?.code === 200;
        if (!acknowledgeSuccess) {
          setAlertStatus({
            message: responseData?.message || "Failed to acknowledge note",
            status: "400",
            origin: "center",
          });
          return;
        }

        setAlertStatus({
          message: responseData?.message || "Note acknowledged successfully",
          status: "200",
          origin: "center",
        });
        setNotes((prevNotes) =>
          prevNotes.map((note) =>
            String(note.noteId) === String(noteId)
              ? {
                  ...note,
                  acknowledgedStatus: true,
                  acknowledgeFlag: 1,
                }
              : note,
          ),
        );
      } catch (error) {
        Sentry.captureException(error);
        setAlertStatus({
          message: "Error while acknowledging note",
          status: "400",
          origin: "center",
        });
      } finally {
        setLoading(false);
      }
    };

    acknowledgeNote();
  };

  return (
    <>
      <Header />
      <div className="home-container">
        <Backdrop sx={{ color: "#ffba30", zIndex: (theme) => theme.zIndex.drawer + 1000 }} open={loading}>
          <img src={require("../../assets/btTree.png")} className="loading-img" alt="Loading" />
        </Backdrop>
        {alertStatus ? <AlertMessage status={alertStatus.status} message={alertStatus.message} origin={alertStatus.origin} closeAlert={() => setAlertStatus("")} /> : null}

        <div className="home-inner-container notes-list-page">
          <div className="heading vitals-heading notes-page-heading">
            <IoChevronBackCircleSharp className="vitals-preview-header" onClick={() => navigate(-1)} />
            <span className="notes-page-title">{residentName ? `${residentName} - Notes` : "Notes"}</span>
            <span></span>
          </div>

          <div className="notes-section notes-cards-wrapper">
            <div className="notes-section-header">
              <h3 className="notes-section-title">Notes</h3>
              <div className="notes-header-actions">
                <button type="button" className={`notes-clear-btn ${activeNotesTab === "all" ? "notes-tab-active" : ""}`} onClick={() => handleTabChange("all")}>
                  Shared Notes
                </button>
                <button type="button" className={`notes-clear-btn ${activeNotesTab === "my" ? "notes-tab-active" : ""}`} onClick={() => handleTabChange("my")}>
                  My Notes
                </button>
              </div>
            </div>
            {visibleNotes.length > 0 ? (
              <div className="notes-section-body">
                <div className="caregiver-notes-single-card">
                  <div className="caregiver-notes-single-card-scroll">
                    {visibleNotes.map((item, index) => (
                      <div key={item.noteId} className="caregiver-note-item" onClick={() => toggleExpanded(item.noteId)}>
                        <div className="caregiver-note-header">
                          <span className="caregiver-note-type">{activeNotesTab === "all" ? `From: ${item.caregiverName || "You"}` : ""}</span>
                          {activeNotesTab === "all" ? <span className="caregiver-note-time">{formatNoteDateTime(item)}</span> : null}
                        </div>
                        <p ref={setMessageNode(String(item.noteId))} className={`caregiver-note-message ${visibleNotes.length === 1 || expandedNotes[item.noteId] ? "expanded" : "collapsed"}`}>
                          {item.description || "No description available"}
                        </p>
                        <div className="all-notes-actions-row">
                          {visibleNotes.length > 1 && (expandedNotes[item.noteId] || truncatedNotes[String(item.noteId)]) ? (
                            <button
                              type="button"
                              className="notes-read-more-btn"
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleExpanded(item.noteId);
                              }}>
                              {expandedNotes[item.noteId] ? "Read less" : "Read more"}
                            </button>
                          ) : (
                            <span />
                          )}
                          <div className="notes-actions-right">
                            {activeNotesTab === "my" ? <span className="caregiver-note-time">{formatNoteDateTime(item)}</span> : null}
                            {activeNotesTab === "all" ? (
                              <button
                                type="button"
                                className={`all-notes-icon-btn ${isNoteAcknowledged(item) ? "liked" : ""}`}
                                disabled={isNoteAcknowledged(item)}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  toggleLikeNote(item.noteId);
                                }}
                                aria-label="Acknowledge note">
                                {isNoteAcknowledged(item) ? <FaThumbsUp /> : <FaRegThumbsUp />}
                              </button>
                            ) : null}
                          </div>
                        </div>
                        {index < visibleNotes.length - 1 ? <div className="caregiver-note-item-divider" /> : null}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="notes-section-empty">{activeNotesTab === "my" ? "No notes added by you for this resident yet." : "No notes available"}</div>
            )}
          </div>
          <div className="task-fab-row notes-fab-row notes-speed-dial">
            <button className="task-extra-fab" onClick={openAddNote} aria-label="Add note">
              <IoMdAdd className="task-extra-fab-icon" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Notes;
